import type React from "react";
import { t } from "ttag";

import { Link } from "metabase/common/components/Link";
import { useSelector } from "metabase/redux";
import { getLocation } from "metabase/selectors/routing";
import {
  Icon,
  NavLink,
  type NavLinkProps,
  Stack,
  type StackProps,
} from "metabase/ui";
import type { IconName } from "metabase-types/api";

// Every admin sub-area (Settings, People, Embedding, Performance, Metabot,
// Tools, ...) renders this alongside the top-level AdminNavbar `<nav>`
// ("Navigation bar") — an unlabelled second <nav> here is a duplicate
// landmark (WCAG 1.3.1, aria_landmark_name_unique). Default label is
// overridable via `stackProps` for a consumer that wants something more
// specific.
export const AdminNavWrapper = ({
  children,
  ...stackProps
}: {
  children: React.ReactNode;
} & StackProps) => {
  return (
    <Stack
      w="16rem"
      gap={0}
      p="md"
      h="100%"
      aria-label={t`Section navigation`}
      {...stackProps}
      component="nav"
    >
      {children}
    </Stack>
  );
};

export type AdminNavItemProps = {
  path?: string;
  /* folderPath is used to highlight the folder when one of its children is active, but doesn't navigate */
  folderPattern?: string;
  icon?: IconName;
  onClick?: () => void;
} & Omit<NavLinkProps, "href">;

export function AdminNavItem({
  path,
  folderPattern,
  label,
  icon,
  ...navLinkProps
}: AdminNavItemProps) {
  const location = useSelector(getLocation);
  const subpath = location?.pathname;

  const sharedProps = {
    defaultOpened: folderPattern ? subpath.includes(folderPattern) : false,
    active: path === subpath,
    variant: "admin-nav" as const,
    label,
    mb: "xs" as const,
    ...(icon ? { leftSection: <Icon name={icon} /> } : undefined),
    ...navLinkProps,
  };

  if (!path) {
    // A folder-only item (e.g. Admin ▸ Settings ▸ Authentication/Appearance)
    // doesn't navigate anywhere — its onClick only expands/collapses its
    // children. Without a real `path`, this used to still render as
    // `component={undefined}`, which falls back to Mantine's own default
    // `component="a"` — combined with this component's `role: "link"`
    // default (NavLink.config.ts), that produced an `<a role="link">` with
    // no `href` at all, which is not in the tab order: a keyboard user
    // could never expand these folders (WCAG 2.1.1,
    // widget_tabbable_exists). A folder toggle is a button, not a link, and
    // a real <button> is keyboard-operable by default.
    return (
      <NavLink
        component="button"
        type="button"
        role="button"
        {...sharedProps}
      />
    );
  }

  return <NavLink component={Link} to={path} {...sharedProps} />;
}
